import { useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { Popover } from '../tasks/linear/Popover';
import { useCompanies } from '../../hooks/useCompanies';

/**
 * Multi-company: firm badge for entity detail views (project, invoice, …).
 *
 * Read-only pill by default; pass `onChange` to turn it into a firm picker
 * backed by the entity's PATCH …/company endpoint. `company === null` means
 * the default firm (NULL companyId on the backend), so the badge shows the
 * default firm's name. Renders nothing on single-firm installs — the firm
 * concept stays invisible there (zero-diff invariant).
 */
export function CompanyBadge({
  company,
  onChange,
  title,
}: {
  company: { id: string; name: string } | null;
  /** Selected firm id, or null for the default firm. */
  onChange?: (companyId: string | null) => Promise<void> | void;
  /** Tooltip explaining what the firm on this entity means. */
  title?: string;
}) {
  const { data: companies } = useCompanies();
  const [pending, setPending] = useState(false);

  if (!companies || companies.length <= 1) return null;

  const defaultCompany = companies.find((c) => c.isDefault);
  const currentId = company?.id ?? defaultCompany?.id;
  const label =
    company?.name ??
    (defaultCompany ? defaultCompany.displayName || defaultCompany.legalName : '—');

  const chipClass =
    'inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-700';

  if (!onChange) {
    return (
      <span className={chipClass} title={title}>
        <Building2 size={12} className="text-gray-400" />
        {label}
      </span>
    );
  }

  async function select(id: string, close: () => void) {
    if (pending || id === currentId) {
      close();
      return;
    }
    setPending(true);
    try {
      // Selecting the default firm stores NULL (the backend's default-firm tag).
      await onChange?.(id === defaultCompany?.id ? null : id);
    } finally {
      setPending(false);
      close();
    }
  }

  return (
    <Popover
      width={240}
      trigger={({ toggle, ref }) => (
        <button
          type="button"
          ref={(el) => ref(el)}
          onClick={toggle}
          disabled={pending}
          title={title}
          className={`${chipClass} hover:border-gray-300 hover:bg-gray-100 disabled:opacity-50`}
        >
          <Building2 size={12} className="text-gray-400" />
          {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      )}
    >
      {(close) => (
        <div className="max-h-64 overflow-y-auto py-1">
          {companies.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => void select(c.id, close)}
              className="ui-menu-item justify-between"
            >
              <span className="truncate">{c.displayName || c.legalName}</span>
              {c.id === currentId && <Check size={14} className="shrink-0 text-gray-400" />}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}
